"use strict";
// Unified transport for identity.flix-store.com. Deliberately knows nothing
// about CSRF tokens, refresh, or "the session expired" — a caller (in
// practice always flix-auth-client.ts) supplies a CSRF token explicitly
// where the backend requires one, and decides what a 401 means. Keeping
// that decision out of this file is what lets flix-auth-client.ts be the
// one place session lifecycle is handled, matching the "one mechanism, not
// two" discipline the backend itself follows (see flix-identity's
// require-authentication.ts).
(function () {
    var apiBaseUrl = null;
    function configure(config) {
        apiBaseUrl = config.baseUrl.replace(/\/+$/, "");
    }
    async function request(path, options) {
        if (!apiBaseUrl) {
            throw new Error("FlixIdentityAPI.configure() must be called before use");
        }
        var method = (options && options.method) || "GET";
        var headers = Object.assign({ Accept: "application/json" }, (options && options.headers) || {});
        var init = { method: method, credentials: "include", headers: headers };
        if (options && options.body !== undefined) {
            headers["Content-Type"] = "application/json";
            init.body = JSON.stringify(options.body);
        }
        var response;
        try {
            response = await fetch(apiBaseUrl + path, init);
        }
        catch (err) {
            var networkError = { status: 0, code: "NETWORK.UNAVAILABLE" };
            throw networkError;
        }
        if (response.status === 204) {
            return undefined;
        }
        var payload = null;
        try {
            payload = await response.json();
        }
        catch (err) {
            payload = null;
        }
        if (!response.ok) {
            var code = payload !== null && typeof payload === "object" && "error" in payload
                ? String(payload.error)
                : "UNKNOWN.ERROR";
            var apiError = { status: response.status, code: code };
            throw apiError;
        }
        return payload;
    }
    function getMe() {
        return request("/me");
    }
    // PATCH /me returns only { accountId, displayName } — deliberately not
    // the full profile (see flix-identity's UpdateProfileUseCase). This
    // signature originally claimed FlixAccountProfile; nothing had called
    // it until the Profile screen, which is when the mismatch surfaced.
    // A real PATCH: only the fields the caller named are put on the wire.
    // Sending the others as undefined would ask the backend to re-validate
    // values nobody edited, and sending them as "" would blank them.
    function updateMe(input) {
        var body = {};
        if (input.displayName !== undefined)
            body.displayName = input.displayName;
        if (input.locale !== undefined)
            body.locale = input.locale;
        if (input.region !== undefined)
            body.region = input.region;
        return request("/me", {
            method: "PATCH",
            body: body,
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function getMarketing() {
        return request("/me/marketing");
    }
    function updateMarketing(input) {
        return request("/me/marketing", {
            // PATCH, not PUT: the CORS allowlist admits GET/HEAD/POST/PATCH/DELETE,
            // so a PUT never leaves the browser.
            method: "PATCH",
            body: { subscribed: input.subscribed },
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function listOrders(input) {
        var params = new URLSearchParams();
        if (input.cursor !== undefined)
            params.set("cursor", input.cursor);
        if (input.limit !== undefined)
            params.set("limit", String(input.limit));
        var query = params.toString();
        return request("/me/orders" + (query ? "?" + query : ""));
    }
    function getOrderDetail(orderId) {
        // orderId is always an opaque id echoed back verbatim from a prior
        // /me/orders response (a Shopify GID, containing literal "/"
        // characters) — encodeURIComponent is required or the route never
        // matches at all. See flix-identity's orders.routes.ts.
        return request("/me/orders/" + encodeURIComponent(orderId));
    }
    /**
     * The files this account was sent. Read-only, so no CSRF token — and no
     * pagination parameters: the backend walks the order history itself and
     * reports `truncated` when it stops short, because a shopper thinks in
     * files, not in pages of orders that may or may not contain one.
     */
    function listDownloads() {
        return request("/me/downloads");
    }
    function listOrderFiles() {
        return request("/me/order-files");
    }
    /**
     * Removes one way of signing in. A write, so it carries the token.
     *
     * The backend refuses to remove the account's last sign-in-eligible
     * method — that rule lives in the Account aggregate, and a 409 here is
     * that refusal, not a transport failure.
     */
    function revokeAuthMethod(input) {
        return request("/me/auth-methods/" + encodeURIComponent(input.authMethodId) + "/revoke", { method: "POST", headers: { "X-CSRF-Token": input.csrfToken } });
    }
    /**
     * The account deletion flow: ask for a code, spend it, then delete.
     *
     * Three calls rather than one because the middle step is what makes the
     * last one permissible — the server records the proof on the session,
     * and DELETE /me refuses without it.
     */
    function startAccountDeletion(input) {
        return request("/me/deletion/step-up", {
            method: "POST",
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function verifyAccountDeletion(input) {
        return request("/me/deletion/step-up/verify", {
            method: "POST",
            body: { challengeId: input.challengeId, code: input.code },
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function deleteAccount(input) {
        return request("/me", {
            method: "DELETE",
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    /**
     * Uploads the picture as raw bytes with its own media type.
     *
     * Not multipart, and not base64 in a JSON body: the backend reads the
     * body as a buffer and the declared type as the claim it checks the
     * magic bytes against, so the simplest possible request is also the one
     * it validates most directly. base64 would cost a third more bytes for
     * nothing. The backend puts the object in R2 itself -- no credential and
     * no presigned upload URL ever reaches this file.
     *
     * `request()` is not reused because it sets Content-Type to
     * application/json whenever a body is present and parses every response
     * as JSON. This needs neither.
     */
    async function uploadAvatar(input) {
        if (!apiBaseUrl) {
            throw new Error("FlixIdentityAPI.configure() must be called before use");
        }
        var response;
        try {
            response = await fetch(apiBaseUrl + "/me/avatar", {
                method: "POST",
                credentials: "include",
                headers: {
                    Accept: "application/json",
                    "Content-Type": input.file.type,
                    "X-CSRF-Token": input.csrfToken,
                },
                body: input.file,
            });
        }
        catch (err) {
            var networkError = { status: 0, code: "NETWORK.UNAVAILABLE" };
            throw networkError;
        }
        var payload = null;
        try {
            payload = await response.json();
        }
        catch {
            payload = null;
        }
        if (!response.ok) {
            var code = payload && typeof payload === "object" && typeof payload.error === "string"
                ? (payload.error)
                : "UNKNOWN.ERROR";
            var apiError = { status: response.status, code: code };
            throw apiError;
        }
        return payload;
    }
    /**
     * Drops the uploaded picture.
     *
     * Answers with what to draw NOW, not a bare confirmation: removing an
     * upload uncovers the provider picture when there is one, and only the
     * backend knows which.
     */
    function removeAvatar(input) {
        return request("/me/avatar", {
            method: "DELETE",
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    /** Where this account is signed in. Read-only, so no CSRF token. */
    function listSessions() {
        return request("/me/sessions");
    }
    /** Ends one session. A write, so it carries the token like every other write here. */
    function revokeSession(input) {
        return request("/me/sessions/" + encodeURIComponent(input.sessionId) + "/revoke", {
            method: "POST",
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function getCsrf() {
        return request("/auth/csrf").then(function (res) {
            return res.csrfToken;
        });
    }
    /**
     * Email sign-in for a caller that is not going to be redirected.
     *
     * These three are the only calls that can create a session, and they do it
     * the same way every other write here works: credentialed, CSRF-token in a
     * header, nothing about the session in the response. The browser is left
     * holding cookies it cannot read; who it now is comes from /me.
     */
    function startEmailSignIn(input) {
        return request("/auth/session/email/start", {
            method: "POST",
            body: { email: input.email },
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function resendEmailSignIn(input) {
        return request("/auth/session/email/resend", {
            method: "POST",
            body: { challengeId: input.challengeId },
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function verifyEmailSignIn(input) {
        return request("/auth/session/email/verify", {
            method: "POST",
            body: { challengeId: input.challengeId, code: input.code },
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    // Adding a proven email to the CURRENT account. Distinct from the
    // /auth/email/* pair, which signs somebody in — see the identity service's
    // link-email.routes.ts for why the two are kept apart.
    function startLinkEmail(input) {
        return request("/me/email/start", {
            method: "POST",
            body: { email: input.email },
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function verifyLinkEmail(input) {
        return request("/me/email/verify", {
            method: "POST",
            body: { challengeId: input.challengeId, code: input.code },
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    function refresh(input) {
        return request("/auth/refresh", {
            method: "POST",
            headers: { "X-CSRF-Token": input.csrfToken },
        }).then(function () {
            return;
        });
    }
    function signOut(input) {
        return request("/auth/sign-out", {
            method: "POST",
            headers: { "X-CSRF-Token": input.csrfToken },
        }).then(function () {
            return;
        });
    }
    // Reuses 1.4's existing "Claim previous purchases" endpoints — not new
    // backend surface — specifically because GET /me/orders' own empty
    // response can't tell "no Shopify link" apart from "linked, zero
    // orders" (both come back as the exact same empty page). detect()
    // already can.
    function detectClaim() {
        return request("/auth/claim/detect");
    }
    function confirmClaim(input) {
        return request("/auth/claim/confirm", {
            method: "POST",
            headers: { "X-CSRF-Token": input.csrfToken },
        });
    }
    window.FlixIdentityAPI = {
        configure: configure,
        request: request,
        getMe: getMe,
        updateMe: updateMe,
        uploadAvatar: uploadAvatar,
        removeAvatar: removeAvatar,
        getMarketing: getMarketing,
        updateMarketing: updateMarketing,
        listSessions: listSessions,
        revokeSession: revokeSession,
        listOrders: listOrders,
        listDownloads: listDownloads,
        listOrderFiles: listOrderFiles,
        revokeAuthMethod: revokeAuthMethod,
        startAccountDeletion: startAccountDeletion,
        verifyAccountDeletion: verifyAccountDeletion,
        deleteAccount: deleteAccount,
        getOrderDetail: getOrderDetail,
        getCsrf: getCsrf,
        startEmailSignIn: startEmailSignIn,
        resendEmailSignIn: resendEmailSignIn,
        verifyEmailSignIn: verifyEmailSignIn,
        refresh: refresh,
        signOut: signOut,
        startLinkEmail: startLinkEmail,
        verifyLinkEmail: verifyLinkEmail,
        detectClaim: detectClaim,
        confirmClaim: confirmClaim,
    };
    // Self-configures from the theme-settings-driven global layout/theme.liquid
    // sets in <head> (before this deferred script runs) — see config/
    // settings_schema.json's "Flix Identity" section. configure() stays
    // exposed too, e.g. for a test harness page that sets its own base URL.
    if (window.flixIdentityConfig && window.flixIdentityConfig.apiBaseUrl) {
        configure({ baseUrl: window.flixIdentityConfig.apiBaseUrl });
    }
})();
